import { CommonModule } from '@angular/common';
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import DOMPurify from 'dompurify';

@Component({
  selector: 'glxy-upgrade-cta-dialog',
  templateUrl: './upgrade-cta-dialog.component.html',
  styleUrls: ['./upgrade-cta-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    TranslateModule,
    GalaxyBadgeModule,
  ],
})
export class UpgradeCTADialogComponent {
  // Writable: partner-center's customize-dialog reassigns config/dialogRef to drive a live preview.
  dialogRef = inject<MatDialogRef<UpgradeCTADialogComponent, UpgradeCTADialogResult>>(MatDialogRef);
  config = inject<UpgradeCTADialogConfig>(MAT_DIALOG_DATA);

  public isLoading = false;
  public hasActiveOrder = false;

  readonly sanitizedSellingPoints: string[] =
    this.config.keySellingPoints?.map((sellingPoint) => this.sanitizeSellingPoint(sellingPoint)) ?? [];
  selectedAddonId: string | undefined = this.config.defaultAddonId;
  readonly selectedAddonIds = new Map<string, string | undefined>();

  sanitizeSellingPoint(sellingPoint: string): string {
    return DOMPurify.sanitize(sellingPoint);
  }

  getSelectedAddonId(editionId: string): string | undefined {
    return this.selectedAddonIds.get(editionId);
  }

  setSelectedAddonId(editionId: string, addonId: string | undefined): void {
    this.selectedAddonIds.set(editionId, addonId);
  }

  onConfirm(): void {
    this.dialogRef.close({ confirmed: true, selectedAddonId: this.selectedAddonId });
  }

  onConfirmEdition(editionId: string): void {
    this.dialogRef.close({
      confirmed: true,
      selectedEditionId: editionId,
      selectedAddonId: this.selectedAddonIds.get(editionId),
    });
  }

  onBuyCredits(editionId: string): void {
    this.dialogRef.close({ confirmed: true, selectedAddonId: this.selectedAddonIds.get(editionId), addonOnly: true });
  }

  onDismiss(): void {
    this.dialogRef.close({ confirmed: false });
  }
}

export interface UpgradeCTADialogConfig {
  imageText?: string;
  imageUrl?: string;
  headerDescription?: string;
  headerSellingPoints?: string[];
  editionName?: string;
  upgradeNowText?: string;
  price?: string;
  frequency?: string;
  keySellingPoints?: string[];
  templateSellingPoints?: TemplateSellingPoint[];
  ctaText?: string;
  activeOrderPrimaryText?: string;
  activeOrderSecondaryText?: string;
  activeOrderImageUrl?: string;
  addons?: UpgradeCTADialogAddon[];
  addonsLabel?: string;
  addonPlaceholder?: string;
  defaultAddonId?: string;
  editions?: UpgradeCTADialogEdition[];
}

export interface TemplateSellingPoint {
  text: string;
  template: object;
}

export interface UpgradeCTADialogAddon {
  readonly id: string;
  readonly label: string;
}

export interface UpgradeCTADialogEdition {
  readonly editionId: string;
  readonly name: string;
  readonly price?: string;
  readonly frequency?: string;
  readonly keySellingPoints?: string[];
  readonly addons?: UpgradeCTADialogAddon[];
  readonly addonsLabel?: string;
  readonly addonPlaceholder?: string;
  readonly isCurrent?: boolean;
  readonly currentLabel?: string;
  readonly buyCreditsLabel?: string;
}

export interface UpgradeCTADialogResult {
  readonly confirmed: boolean;
  readonly selectedAddonId?: string;
  readonly selectedEditionId?: string;
  readonly addonOnly?: boolean;
}
